import { DragOverlay, type Over } from '@dnd-kit/core';
import { restrictToWindowEdges, snapCenterToCursor } from '@dnd-kit/modifiers';
import { add } from 'date-fns';
import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { getTaskStatusColor } from '../utils';
import { useFormatTaskDuration } from '../../ProductionPlans/Gantt/hooks/useFormatTaskDuration';
import { useTimelineStore } from '../../ProductionPlans/Gantt/store';
import type { DroppableData } from '../../ProductionPlans/Gantt/types';
import { GanttConfig } from '../../ProductionPlans/Gantt/GanttConfig';
import { NodeSingleBase } from '../../ProductionPlans/Gantt/components';
import { hasNodeOverdue } from '../../ProductionPlans/Gantt/utils';
import { useProductionPlanSettings } from '../../ProductionPlans/components/SettingsDrawer';
import type { NodeFlags } from '../../ProductionPlans/Gantt/mappers';
import type { TaskToDoEntry } from '@nexelem/graphne-api';

type NodeItemListOverlayProps = {
  task: TaskToDoEntry;
  over: Over | null;
};
export const NodeItemListOverlay = ({ task, over }: NodeItemListOverlayProps) => {
  const formatTaskDuration = useFormatTaskDuration({ compact: true });

  const showOnTask = useProductionPlanSettings((state) => state.gantt.showOnTask);
  const currentFilter = useTimelineStore((state) => state.filters.current);
  const taskWidth =
    (task.estimatedDurations.median / (currentFilter.measureScale * 60 * 60)) * GanttConfig.SINGLE_ELEMENT_WIDTH;

  const startDate = (over?.data.current as DroppableData)?.endDate ?? new Date();
  const endDate = add(startDate, { seconds: task.estimatedDurations.median });

  const taskStatusColor = useMemo(() => getTaskStatusColor(task.status), [task.status]);

  const nodeFlags = flatListTaskNodeFlagsMapper(task, endDate);

  return createPortal(
    <DragOverlay
      adjustScale={false}
      className="flat-task-form-control-overlay"
      modifiers={[restrictToWindowEdges, snapCenterToCursor]}
    >
      <NodeSingleBase
        isDraggable
        className="h-11"
        current={0}
        date={formatTaskDuration({ endDate, startDate })}
        hasError={hasNodeOverdue(task.dueDate, endDate)}
        id={task.id}
        nodeFlags={nodeFlags}
        showOnTask={showOnTask}
        statusColor={taskStatusColor || 'grey'}
        title={task.name ?? task.productName}
        total={task.totalAmount.amount}
        unit={task.totalAmount.unit.name}
        width={taskWidth || 200}
      />
    </DragOverlay>,
    document.body,
  );
};

const flatListTaskNodeFlagsMapper = (task: TaskToDoEntry, endDate: Date): NodeFlags => {
  return {
    notEqualMultinode: false,
    retooling: false,
    paused: false,
    downtime: false,
    frozen: false,
    toPublish: true,
    priority: false,
    warehouseShortage: false,
    overdue: hasNodeOverdue(task.dueDate, endDate),
    comment: (task?.comments?.length ?? 0) > 0,
  };
};
